import type { AssessmentNodes } from "../../../../types/assessment/data/index.ts";
import { Monitor } from "../../../../types/assessment/monitor.ts";
import { credentialFixGenerator } from "./gcloud-fix-generators.ts";

export const CredentialPresets = {
  "Unused Service-Account Keys (Last 40 days)": Monitor<
    AssessmentNodes,
    "credential"
  >({
    scopes: ["gcloud"],
    show: "credential",
    searchTerm:
      'credential={enabledKey:"true" last40:"unused"}->identity={type:"service-account" status:"active"}',
    label: "Unused service-account key (last 40 days) ",
    priority: "HIGH",
    cta: "This service-account key has have not been used in the last 40 days. Consider removing this key.",
    remediation: (action: string) =>
      `This service-account key has not been used in the last 40 days. P0 has automatically ${action} to disable this key.`,
    description:
      "Service-account keys that have not been used in the last 40 days.",
    fix: credentialFixGenerator("enabled"),
    revert: credentialFixGenerator("disabled"),
  }),
  "Unused Service-Account Keys (Last 365 days)": Monitor<
    AssessmentNodes,
    "credential"
  >({
    scopes: ["gcloud"],
    show: "credential",
    searchTerm:
      'credential={enabledKey:"true" last365:"unused"}->identity={type:"service-account" status:"active"}',
    label: "Unused service-account key (last 365 days)",
    priority: "HIGH",
    cta: "This service-account key has have not been used in the last 365 days. Consider removing this key.",
    remediation: (action: string) =>
      `This service-account key has not been used in the last 365 days. P0 has automatically ${action} to disable this key.`,
    description:
      "Service-account keys that have not been used in the last 365 days.",
    fix: credentialFixGenerator("enabled"),
    revert: credentialFixGenerator("disabled"),
  }),
  "Stale Service-Account Key": Monitor<AssessmentNodes, "credential">({
    scopes: ["gcloud"],
    show: "credential",
    searchTerm: 'credential:stale90:"true"->identity=type:"service-account"',
    label: "Stale service-account key",
    priority: "HIGH",
    cta: "This service-account key has not been rotated in the last 90 days. Google recommends rotating keys every 90 days.",
    description:
      "Service-account keys that have not been rotated in the last 90 days.",
    management: {
      description:
        "P0 will automatically rotate your service account keys every 90 days",
      inputPrompt:
        "Please enter the Google Secret Manager resource name for the secret containing keys for this service account",
    },
  }),
  "Stale Azure Service Principal Credentials": Monitor<
    AssessmentNodes,
    "credential"
  >({
    scopes: ["azure"],
    show: "credential",
    searchTerm:
      'credential={type:"key" stale90:"true"}->identity=type:"service-account"',
    label: "Stale Azure service principal credential",
    priority: "HIGH",
    cta: "This service principal credential has not been rotated in the last 90 days. Consider rotating this credential.",
    description:
      "Azure service principal credentials that have not been rotated in the last 90 days.",
  }),
  "Stale Entra App Registration Credentials": Monitor<
    AssessmentNodes,
    "credential"
  >({
    scopes: ["entra-id"],
    show: "credential",
    searchTerm:
      'credential={type:"key" stale90:"true"}->identity=type:"entra-app-registration"',
    label: "Stale Entra app registration credential",
    priority: "HIGH",
    cta: "This app registration credential has not been rotated in the last 90 days. Consider rotating this credential.",
    description:
      "Entra app registration credentials that have not been rotated in the last 90 days.",
  }),
  "Expiring Entra App Registration Credentials": Monitor<
    AssessmentNodes,
    "credential"
  >({
    scopes: ["entra-id"],
    show: "credential",
    searchTerm:
      'credential={type:"key" expiryIn30:"true"}->identity=type:"entra-app-registration"',
    label: "Expiring Entra app registration credential",
    priority: "HIGH",
    cta: "This app registration credential expires within 30 days. Rotate or replace this credential before it expires.",
    description:
      "Entra app registration credentials that expire within 30 days.",
  }),
  "Long-Lived Entra App Registration Credentials": Monitor<
    AssessmentNodes,
    "credential"
  >({
    scopes: ["entra-id"],
    show: "credential",
    searchTerm:
      'credential={type:"key" expiryAfter365:"true"}->identity=type:"entra-app-registration"',
    label: "Long-lived Entra app registration credential",
    priority: "MEDIUM",
    cta: "This app registration credential expires more than 365 days from now. Consider replacing it with a shorter-lived credential.",
    description:
      "Entra app registration credentials with expiration dates more than 365 days away.",
  }),
  "Wildcard-Subject Federated Credentials": Monitor<
    AssessmentNodes,
    "credential"
  >({
    scopes: ["azure"],
    show: "credential",
    searchTerm:
      'credential={type:"federated" wildcardSubject:"true"}->identity=type:"service-account"',
    label: "Federated credential with wildcard subject",
    priority: "MEDIUM",
    cta: "This federated identity credential has no subject filter (missing, empty, or wildcard), so any workload token from the trusted issuer can authenticate as this service principal. Scope the subject to a specific workload.",
    description:
      "Federated identity credentials whose subject claim filter is missing, empty, or contains a wildcard — permitting any token from the issuer to authenticate.",
  }),
  "Privileged Federated Service Principals": Monitor<
    AssessmentNodes,
    "credential"
  >({
    scopes: ["azure"],
    show: "credential",
    searchTerm:
      'credential=type:"federated"->identity={type:"service-account"}->grant=role:/^(Owner|Contributor)$/',
    label: "Privileged federated service principal",
    priority: "MEDIUM",
    cta: "This service principal can be assumed by an external system via workload identity federation and holds a privileged role (Owner or Contributor). Compromise of the external issuer grants privileged access to this scope. Reduce the role or tighten the federation trust.",
    description:
      "Federated identity credentials on service principals that hold Owner or Contributor, exposing privileged access to an external federation issuer.",
  }),
  "Existing Access Keys for Root User": Monitor<AssessmentNodes, "credential">({
    scopes: ["aws"],
    show: "credential",
    searchTerm:
      'credential={enabledKey:"true" type:"key"}->identity=label:"root"',
    label: "Existing access keys for root user",
    priority: "CRITICAL",
    cta: `The root user's IAM access keys exist. It is highly recommended to remove these keys due to"
    security best practices. Note: The user key shown is a placeholder. This monitor only alerts you that 
    at least one key exists—it does not reflect the actual key.`,
    description: "Root user IAM access keys that exist.",
  }),
  "Stale IAM User Access Key": Monitor<AssessmentNodes, "credential">({
    scopes: ["aws"],
    show: "credential",
    searchTerm: 'credential={stale90:"true" type:"key"}->identity:',
    label: "Stale access key",
    priority: "HIGH",
    cta: "This service-account key has not been rotated in the last 90 days. AWS recommends rotating keys every 90 days.",
    description:
      "IAM access keys that have not been rotated in the last 90 days.",
  }),
  "Unused IAM User Access Keys (Last 90 days)": Monitor<
    AssessmentNodes,
    "credential"
  >({
    scopes: ["aws"],
    show: "credential",
    searchTerm:
      'credential={enabledKey:"true" last90:"unused" type:"key"}->identity:',
    label: "Unused access key (last 90 days)",
    priority: "HIGH",
    cta: "This IAM user's access key has not been used in the last 90 days. It is recommended to remove unused keys.",
    description: "IAM access keys that have not been used in the last 90 days.",
  }),
  "Stale IAM User Password": Monitor<AssessmentNodes, "credential">({
    scopes: ["aws"],
    show: "credential",
    searchTerm: 'credential={type:"password" stale90:"true"}->identity:',
    label: "Stale IAM User password",
    priority: "MEDIUM",
    cta: "This IAM user's password has not been changed in 90+ days. AWS recommends regular password rotation.",
    description:
      "IAM passwords that have not been changed in the last 90 days.",
  }),
  "Unused IAM User Password": Monitor<AssessmentNodes, "credential">({
    scopes: ["aws"],
    show: "credential",
    searchTerm: 'credential={type:"password" last90:"unused"}->identity:',
    label: "Unused IAM User password",
    priority: "MEDIUM",
    cta: "This IAM user's password has not been used in 90+ days. It is recommended to remove this form of authentication.",
    description: "IAM passwords that have not been used in the last 90 days.",
  }),
};
